import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox, TextField, MenuItem,
  Chip, CircularProgress, Alert, Tooltip, Divider, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  RadioGroup, Radio, FormControlLabel, FormControl,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const ANIOS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
const MESES = [
  { v: 1, l: 'Enero' }, { v: 2, l: 'Febrero' }, { v: 3, l: 'Marzo' },
  { v: 4, l: 'Abril' }, { v: 5, l: 'Mayo' }, { v: 6, l: 'Junio' },
  { v: 7, l: 'Julio' }, { v: 8, l: 'Agosto' }, { v: 9, l: 'Septiembre' },
  { v: 10, l: 'Octubre' }, { v: 11, l: 'Noviembre' }, { v: 12, l: 'Diciembre' },
];
const TIPOS_CC = [
  { value: '01', label: '01 — Acepta propuesta SUNAT' },
  { value: '02', label: '02 — Reemplaza propuesta' },
  { value: '03', label: '03 — Ajustes posteriores al período' },
  { value: '04', label: '04 — Ajustes períodos anteriores — Formato General' },
  { value: '05', label: '05 — Ajustes períodos anteriores — Formato Simplificado' },
];
const TIPO_MAP = { '01': 'Factura', '03': 'Boleta', '07': 'NC', '08': 'ND', '02': 'RHE' };
const ORIGEN_FILTROS = [
  { v: 'TODOS', l: 'Todos' },
  { v: 'DIFIERE', l: 'Difieren' },
  { v: 'SOLO_SUNAT', l: 'Solo SUNAT' },
  { v: 'SOLO_ERP', l: 'Solo ERP' },
  { v: 'AMBOS', l: 'Coinciden' },
];

function fmtMon(v) {
  return Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtFecha(d) {
  if (!d) return '';
  const s = String(d).substring(0, 10);
  const [y, m, dd] = s.split('-');
  if (!y || !m || !dd) return s;
  return `${dd}/${m}/${y}`;
}

function clave(tipo, serie, numero) {
  return `${tipo}|${(serie || '').toUpperCase()}|${String(numero || '').replace(/^0+/, '') || '0'}`;
}

function compararListas(erpList, sunatList) {
  const mapErp   = new Map();
  const mapSunat = new Map();
  for (const e of erpList)   mapErp.set(clave(e.tipo_comprobante, e.serie, e.numero), e);
  for (const s of sunatList) mapSunat.set(clave(s.tipoCdp, s.serie, s.numero), s);

  const claves = new Set([...mapErp.keys(), ...mapSunat.keys()]);
  const result = [];
  for (const k of claves) {
    const e = mapErp.get(k);
    const s = mapSunat.get(k);
    let origen;
    if (e && s) {
      // Un comprobante anulado/rechazado en el ERP legítimamente aparece con monto
      // distinto (0) en SUNAT — no es un descuadre real, así que no se marca DIFIERE.
      const erpAnulado = e.estado_cpe === 'ANULADO' || e.estado_cpe === 'RECHAZADO';
      // Notas de Crédito/Débito: SUNAT las devuelve con el total en NEGATIVO en su
      // propuesta real (confirmado con datos reales — NC de S/1650 en el ERP vino
      // como -1650 desde SUNAT), mientras que en el ERP se guarda como magnitud
      // positiva. Comparar por magnitud (valor absoluto) evita un "DIFIERE" falso
      // por una simple diferencia de signo/convención entre ambos lados.
      const diff = Math.abs(Math.abs(Number(e.total || 0)) - Math.abs(Number(s.total || 0)));
      origen = (!erpAnulado && diff > 0.05) ? 'DIFIERE' : 'AMBOS';
    } else if (e) {
      origen = 'SOLO_ERP';
    } else {
      origen = 'SOLO_SUNAT';
    }
    result.push({ erp: e || null, sunat: s || null, origen });
  }
  const ord = { DIFIERE: 0, SOLO_SUNAT: 1, SOLO_ERP: 2, AMBOS: 3 };
  result.sort((a, b) => (ord[a.origen] ?? 9) - (ord[b.origen] ?? 9));
  return result;
}

function chipOrigen(o) {
  if (o === 'AMBOS')      return <Chip label="COINCIDE" color="success" size="small" sx={{ fontWeight: 'bold' }} />;
  if (o === 'DIFIERE')    return <Chip label="DIFIERE"  color="error"   size="small" sx={{ fontWeight: 'bold' }} />;
  if (o === 'SOLO_ERP')   return <Chip label="SOLO ERP" color="info"    size="small" sx={{ fontWeight: 'bold' }} />;
  if (o === 'SOLO_SUNAT') return <Chip label="SOLO SUNAT" sx={{ bgcolor: '#ff9800', color: '#fff', fontWeight: 'bold' }} size="small" />;
  return null;
}

export default function SireVentasDeclaracion() {
  const now = new Date();
  const [anio,      setAnio]      = useState(now.getFullYear());
  const [mes,       setMes]       = useState(now.getMonth() + 1);
  const [tipoCC,    setTipoCC]    = useState('02');
  const [corr,      setCorr]      = useState('01');
  const [erpRows,   setErpRows]   = useState([]);
  const [sunatRows, setSunatRows] = useState([]);
  const [rows,      setRows]      = useState([]);   // comparación
  const [selected,  setSelected]  = useState(new Set());       // ids ERP seleccionados
  const [selectedSunat, setSelectedSunat] = useState(new Set()); // claves SOLO_SUNAT seleccionadas
  const [filtroOrigen, setFiltroOrigen] = useState('TODOS');
  const [loadingErp,   setLoadingErp]   = useState(false);
  const [loadingSunat, setLoadingSunat] = useState(false);
  const [loadingTxt,   setLoadingTxt]   = useState(false);
  const [generando,    setGenerando]    = useState(false);
  const [error,        setError]        = useState('');
  const [dlgTxtAbierto, setDlgTxtAbierto] = useState(false);
  const [modoAnulados,  setModoAnulados]  = useState('normal'); // 'normal' | 'anonimo'
  const fileRef = useRef(null);

  const esAjuste = ['03', '04', '05'].includes(tipoCC);

  const actualizar = (erp, sunat) => {
    const comparacion = compararListas(erp, sunat);
    setRows(comparacion);
    // Pre-seleccionar todos los rows que tienen id ERP
    const presel = new Set(
      comparacion.filter(r => r.erp?.id).map(r => r.erp.id)
    );
    setSelected(presel);
  };

  const handleCargarErp = async () => {
    setLoadingErp(true); setError('');
    try {
      const res = await axios.get(`/api/sire/ventas?anio=${anio}&mes=${mes}`);
      const erp = res.data || [];
      setErpRows(erp);
      actualizar(erp, sunatRows);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar datos ERP');
    } finally { setLoadingErp(false); }
  };

  const handleTraerSunat = async () => {
    setLoadingSunat(true); setError('');
    try {
      const res = await axios.get(`/api/sire/ventas/propuesta-sunat?anio=${anio}&mes=${mes}`);
      const sunat = res.data || [];
      setSunatRows(sunat);
      actualizar(erpRows, sunat);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al obtener propuesta SUNAT');
    } finally { setLoadingSunat(false); }
  };

  const handleSubirTxt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setLoadingTxt(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post('/api/sire/ventas/parsear-txt', fd);
      const sunat = res.data || [];
      setSunatRows(sunat);
      actualizar(erpRows, sunat);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al parsear TXT');
    } finally { setLoadingTxt(false); }
  };

  const filasFiltradas = filtroOrigen === 'TODOS' ? rows : rows.filter(r => r.origen === filtroOrigen);
  const idsErpSeleccionados = [...selected];

  // Clave de selección para filas SOLO_SUNAT (no tienen id ERP)
  const sunatKeyOf = (r) => (!r.erp?.id && r.sunat) ? clave(r.sunat.tipoCdp, r.sunat.serie, r.sunat.numero) : null;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const ids  = filasFiltradas.filter(r => r.erp?.id).map(r => r.erp.id);
      const keys = filasFiltradas.map(sunatKeyOf).filter(Boolean);
      setSelected(prev => { const n = new Set(prev); ids.forEach(id => n.add(id)); return n; });
      setSelectedSunat(prev => { const n = new Set(prev); keys.forEach(k => n.add(k)); return n; });
    } else {
      const ids  = new Set(filasFiltradas.filter(r => r.erp?.id).map(r => r.erp.id));
      const keys = new Set(filasFiltradas.map(sunatKeyOf).filter(Boolean));
      setSelected(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
      setSelectedSunat(prev => { const n = new Set(prev); keys.forEach(k => n.delete(k)); return n; });
    }
  };

  const handleSelectRow = (id) => {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const handleSelectSunatRow = (key) => {
    setSelectedSunat(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  };

  const handleDescargar = async (formato, anonimizarAnulados = false) => {
    const sunatSeleccionadas = rows.filter(r => sunatKeyOf(r) && selectedSunat.has(sunatKeyOf(r))).map(r => r.sunat);
    if (idsErpSeleccionados.length === 0 && sunatSeleccionadas.length === 0) return;
    setGenerando(true);
    try {
      const corrParam = esAjuste ? String(corr).padStart(2, '0') : '00';
      // Solo se usa para el Excel — pinta la columna "Comparativo"
      // (COINCIDE/DIFIERE/SOLO ERP/SOLO SUNAT) con lo que ya calculó esta pantalla.
      const origenPorClave = {};
      rows.forEach(r => {
        const t = r.erp?.tipo_comprobante || r.sunat?.tipoCdp;
        const s = r.erp?.serie || r.sunat?.serie;
        const n = r.erp?.numero || r.sunat?.numero;
        if (t) origenPorClave[clave(t, s, n)] = r.origen;
      });
      const res = await axios.post('/api/sire/ventas/descargar', {
        anio, mes, tipo: tipoCC, formato, corr: corrParam,
        ids: idsErpSeleccionados,
        extraSunat: sunatSeleccionadas,
        anonimizarAnulados, // solo tiene efecto en el TXT — el Excel ignora este campo
        origenPorClave,     // solo tiene efecto en el Excel
      }, { responseType: 'blob' });
      const cd  = res.headers['content-disposition'] || '';
      const fn  = (cd.match(/filename="([^"]+)"/) || [])[1] || `SIRE_RVIE_${anio}${String(mes).padStart(2,'0')}.${formato}`;
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = blobUrl; a.download = fn; a.click();
      URL.revokeObjectURL(blobUrl);
    } catch { setError('Error al generar el archivo'); }
    finally { setGenerando(false); }
  };

  // El botón TXT siempre pasa primero por el diálogo (Excel se descarga directo,
  // sin opciones — siempre con los datos reales del cliente, como pidió el usuario).
  const handleClickTxt = () => { setModoAnulados('normal'); setDlgTxtAbierto(true); };
  const handleConfirmarTxt = () => {
    setDlgTxtAbierto(false);
    handleDescargar('txt', modoAnulados === 'anonimo');
  };

  // Contadores
  const cntAmbos    = rows.filter(r => r.origen === 'AMBOS').length;
  const cntDifiere  = rows.filter(r => r.origen === 'DIFIERE').length;
  const cntSoloErp  = rows.filter(r => r.origen === 'SOLO_ERP').length;
  const cntSolSunat = rows.filter(r => r.origen === 'SOLO_SUNAT').length;

  const totalSeleccionados = selected.size + selectedSunat.size;

  const selFiltradas = filasFiltradas.filter(r => r.erp?.id && selected.has(r.erp.id));
  const seleccionablesFiltradas = filasFiltradas.filter(r => r.erp?.id || sunatKeyOf(r));
  const estaSeleccionada = (r) => r.erp?.id ? selected.has(r.erp.id) : (sunatKeyOf(r) ? selectedSunat.has(sunatKeyOf(r)) : false);
  const allFilSel    = seleccionablesFiltradas.length > 0 && seleccionablesFiltradas.every(estaSeleccionada);
  const someFilSel   = seleccionablesFiltradas.some(estaSeleccionada) && !allFilSel;

  const sunatSelFiltradas = filasFiltradas.filter(r => sunatKeyOf(r) && selectedSunat.has(sunatKeyOf(r)));
  // Anulado/rechazado: se muestra en 0 en el resumen (solo visual, no afecta el TXT/Excel generado)
  const esAnuladoOrechazado = (estadoCpe) => estadoCpe === 'ANULADO' || estadoCpe === 'RECHAZADO';
  const totSelBase = selFiltradas.reduce((s, r) => s + (esAnuladoOrechazado(r.erp?.estado_cpe) ? 0 : Number(r.erp?.total_gravada || 0)), 0)
                    + sunatSelFiltradas.reduce((s, r) => s + Number(r.sunat?.base || 0), 0);
  const totSelIgv  = selFiltradas.reduce((s, r) => s + (esAnuladoOrechazado(r.erp?.estado_cpe) ? 0 : Number(r.erp?.total_igv || 0)), 0)
                    + sunatSelFiltradas.reduce((s, r) => s + Number(r.sunat?.igv || 0), 0);
  const totSelTot  = selFiltradas.reduce((s, r) => s + (esAnuladoOrechazado(r.erp?.estado_cpe) ? 0 : Number(r.erp?.total || 0)), 0)
                    + sunatSelFiltradas.reduce((s, r) => s + Number(r.sunat?.total || 0), 0);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Cabecera */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <ReceiptLongIcon color="primary" />
        <Typography variant="h6" fontWeight="bold">SIRE — Ventas Declaración (RVIE)</Typography>
        <Chip label="Comparativo ERP vs SUNAT" size="small" color="primary" variant="outlined" />
      </Box>

      {/* Filtros y acciones */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField select label="Año" value={anio} onChange={e => setAnio(Number(e.target.value))}
            size="small" sx={{ minWidth: 100 }}>
            {ANIOS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField select label="Mes" value={mes} onChange={e => setMes(Number(e.target.value))}
            size="small" sx={{ minWidth: 130 }}>
            {MESES.map(m => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
          </TextField>

          <Button variant="contained" color="primary" startIcon={<SearchIcon />}
            onClick={handleCargarErp} disabled={loadingErp} sx={{ height: 40 }}>
            {loadingErp ? <CircularProgress size={18} color="inherit" /> : 'Cargar ERP'}
          </Button>

          <Divider orientation="vertical" flexItem />

          <Tooltip title="Obtiene la propuesta SUNAT usando las credenciales configuradas en la empresa">
            <Button variant="outlined" color="primary" startIcon={<CloudDownloadIcon />}
              onClick={handleTraerSunat} disabled={loadingSunat} sx={{ height: 40 }}>
              {loadingSunat ? <CircularProgress size={18} color="inherit" /> : 'Propuesta SUNAT (API)'}
            </Button>
          </Tooltip>

          <Tooltip title="Sube el TXT descargado del portal SOL de SUNAT">
            <Button variant="outlined" color="secondary" startIcon={<UploadFileIcon />}
              onClick={() => fileRef.current?.click()} disabled={loadingTxt} sx={{ height: 40 }}>
              {loadingTxt ? <CircularProgress size={18} color="inherit" /> : 'Subir TXT SUNAT'}
            </Button>
          </Tooltip>
          <input ref={fileRef} type="file" accept=".txt" hidden onChange={handleSubirTxt} />

          <Divider orientation="vertical" flexItem />

          <TextField select label="Tipo CC" value={tipoCC} onChange={e => setTipoCC(e.target.value)}
            size="small" sx={{ minWidth: 290 }}>
            {TIPOS_CC.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>

          {esAjuste && (
            <TextField label="Corr. ajuste (NN)" value={corr}
              onChange={e => setCorr(e.target.value.replace(/\D/g, '').slice(0, 2))}
              size="small" sx={{ minWidth: 140 }} inputProps={{ maxLength: 2 }} helperText="2 dígitos" />
          )}

          <Tooltip title={totalSeleccionados === 0 ? 'Selecciona al menos 1 fila' : ''}>
            <span>
              <Button variant="outlined" color="primary" startIcon={<DownloadIcon />}
                onClick={handleClickTxt}
                disabled={totalSeleccionados === 0 || generando} sx={{ height: 40 }}>
                {generando ? <CircularProgress size={16} /> : 'TXT'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={totalSeleccionados === 0 ? 'Selecciona al menos 1 fila' : ''}>
            <span>
              <Button variant="outlined" color="success" startIcon={<TableChartIcon />}
                onClick={() => handleDescargar('excel')}
                disabled={totalSeleccionados === 0 || generando} sx={{ height: 40 }}>
                Excel
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Resumen */}
      {rows.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip label={`✅ COINCIDE: ${cntAmbos}`}    color="success" variant="outlined" />
          <Chip label={`⚠️ DIFIERE: ${cntDifiere}`}   color="error"   variant="outlined" />
          <Chip label={`🔵 SOLO ERP: ${cntSoloErp}`}  color="info"    variant="outlined" />
          <Chip label={`🟡 SOLO SUNAT: ${cntSolSunat}`} sx={{ borderColor: '#ff9800', color: '#ff9800' }} variant="outlined" />
          {totalSeleccionados > 0 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography variant="body2" color="text.secondary">
                <b>{totalSeleccionados}</b> sel ({idsErpSeleccionados.length} ERP + {selectedSunat.size} SUNAT) · Base: <b>S/ {fmtMon(totSelBase)}</b> · IGV: <b>S/ {fmtMon(totSelIgv)}</b> · Total: <b>S/ {fmtMon(totSelTot)}</b>
              </Typography>
            </>
          )}

          <Box sx={{ ml: 'auto' }}>
            <TextField select label="Filtrar" value={filtroOrigen} onChange={e => setFiltroOrigen(e.target.value)}
              size="small" sx={{ minWidth: 140 }}>
              {ORIGEN_FILTROS.map(f => <MenuItem key={f.v} value={f.v}>{f.l}</MenuItem>)}
            </TextField>
          </Box>
        </Box>
      )}

      {/* Tabla */}
      {filasFiltradas.length > 0 && (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={allFilSel} indeterminate={someFilSel} onChange={handleSelectAll} color="primary" />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Serie</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Número</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cliente / RUC</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Total ERP</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Total SUNAT</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Diferencia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filasFiltradas.map((r, idx) => {
                const e = r.erp;
                const s = r.sunat;
                const id = e?.id;
                const sunatKey = sunatKeyOf(r);
                const isSel = id ? selected.has(id) : (sunatKey ? selectedSunat.has(sunatKey) : false);
                const tipoLabel = TIPO_MAP[e?.tipo_comprobante || s?.tipoCdp || ''] || e?.tipo_comprobante || s?.tipoCdp || '—';
                const serie  = e?.serie  || s?.serie  || '—';
                const numero = e?.numero || s?.numero || '—';
                const fecha  = e?.fecha_emision ? fmtFecha(e.fecha_emision) : fmtFecha(s?.fecha);
                const nombre = e?.cliente_nombre || s?.nombre || '—';
                const ruc    = e?.cliente_ruc    || s?.rucDoc  || '';
                const totE   = e ? Number(e.total || 0) : null;
                // SUNAT devuelve el total de NC/ND en negativo; el ERP lo guarda como
                // magnitud positiva — se muestra siempre en positivo (igual que "Total
                // ERP") para que ambas columnas sean directamente comparables a simple
                // vista, y la diferencia se calcula por magnitud, no por valor con signo.
                const totS   = s ? Math.abs(Number(s.total || 0)) : null;
                const erpAnulado = e?.estado_cpe === 'ANULADO' || e?.estado_cpe === 'RECHAZADO';
                const diff   = (totE !== null && totS !== null && !erpAnulado) ? Math.abs(Math.abs(totE) - totS) : null;

                const rowBg = r.origen === 'DIFIERE'    ? 'rgba(255,23,68,0.06)'
                            : r.origen === 'SOLO_SUNAT' ? 'rgba(255,152,0,0.07)'
                            : undefined;

                return (
                  <TableRow key={idx} hover selected={!!isSel}
                    onClick={() => { if (id) handleSelectRow(id); else if (sunatKey) handleSelectSunatRow(sunatKey); }}
                    sx={{ cursor: (id || sunatKey) ? 'pointer' : 'default', bgcolor: rowBg,
                          opacity: r.origen === 'SOLO_SUNAT' ? 0.85 : 1 }}>
                    <TableCell padding="checkbox">
                      {id ? (
                        <Checkbox checked={!!isSel} color="primary"
                          onClick={ev => ev.stopPropagation()} onChange={() => handleSelectRow(id)} />
                      ) : sunatKey ? (
                        <Checkbox checked={!!isSel} color="warning"
                          onClick={ev => ev.stopPropagation()} onChange={() => handleSelectSunatRow(sunatKey)} />
                      ) : <Box sx={{ width: 42 }} />}
                    </TableCell>
                    <TableCell>{chipOrigen(r.origen)}</TableCell>
                    <TableCell>
                      <Chip label={tipoLabel} size="small" variant="outlined"
                        color={erpAnulado ? 'error' : (['01','03'].includes(e?.tipo_comprobante || s?.tipoCdp) ? 'primary' : 'default')} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{serie}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{numero}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fecha}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" noWrap>{nombre}</Typography>
                      {ruc && <Typography variant="caption" color="text.secondary">{ruc}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {totE !== null ? `S/ ${fmtMon(erpAnulado ? 0 : totE)}` : <Typography color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {totS !== null ? `S/ ${fmtMon(totS)}` : <Typography color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: diff > 0.05 ? 'bold' : 'normal', color: diff > 0.05 ? 'error.main' : 'text.secondary' }}>
                      {diff !== null && diff > 0.005 ? `S/ ${fmtMon(diff)}` : diff !== null ? '—' : ''}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loadingErp && rows.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography color="text.secondary">
            Selecciona año y mes, presiona <b>Cargar ERP</b> y luego <b>Propuesta SUNAT (API)</b> o <b>Subir TXT SUNAT</b> para comparar.
          </Typography>
        </Paper>
      )}

      <Dialog open={dlgTxtAbierto} onClose={() => setDlgTxtAbierto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generar TXT para SUNAT</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            ¿Cómo quieres declarar los comprobantes anulados o rechazados en este TXT?
            El Excel siempre se genera con los datos reales del cliente — esta opción es solo para el TXT.
          </DialogContentText>
          <FormControl>
            <RadioGroup value={modoAnulados} onChange={e => setModoAnulados(e.target.value)}>
              <FormControlLabel value="normal" control={<Radio />} label={
                <Box>
                  <Typography variant="body2" fontWeight="bold">Con los datos reales del cliente</Typography>
                  <Typography variant="caption" color="text.secondary">
                    RUC/DNI y razón social del cliente tal cual (montos en 0.00, como corresponde a un anulado).
                  </Typography>
                </Box>
              } sx={{ alignItems: 'flex-start', mb: 1 }} />
              <FormControlLabel value="anonimo" control={<Radio />} label={
                <Box>
                  <Typography variant="body2" fontWeight="bold">Con identidad genérica para anulados</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tipo doc. 0, número 0001, razón social "COMPROBANTE DE VENTA ANULADO" (montos en 0.00).
                  </Typography>
                </Box>
              } sx={{ alignItems: 'flex-start' }} />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgTxtAbierto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmarTxt}>Generar TXT</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
